import axios from 'axios'
import store from '@/store/store'
import {router} from '@/router/router.js'
import {indexedStore} from '@/indexedDB/indexedStore'
import {settings} from '@/configs/settings.js'
import {subscriptionFeatures} from '@/configs/subscriptionStatuses.js'
import {TIME_UNITS} from '@/utils/timeLeft'

// Временный хардкод, пока бэк не готов с /api/promo-campaigns/
// Без ?slug= — оба тарифа (уже разданные ссылки /payment-form-promo)
const PROMO_SUBSCRIPTIONS_DEFAULT = {
  'Практикум по таблицам': {
    with_subscription: false,
    description: 'Доступ только к практикуму по таблицам',
    offer_url: 'https://younesis.com/oferta_tables',
  },
  'Younesis Premium 12 месяцев': {
    with_subscription: true,
    description: 'Доступ к подписке Younesis + практикуму по таблицам',
    offer_url: 'https://younesis.com/oferta_app',
    is_universal: true,
  },
}

// С ?slug= — свой набор на каждый slug
const PROMO_SUBSCRIPTIONS_BY_SLUG = {
  'oferta_tables': {
    'Практикум по таблицам': {
      with_subscription: false,
      description: 'Доступ только к практикуму по таблицам',
      offer_url: 'https://younesis.com/oferta_tables',
    },
    'Younesis Premium 12 месяцев': {
      with_subscription: true,
      description: 'Доступ к подписке Younesis + практикуму по таблицам',
      offer_url: 'https://younesis.com/oferta_app',
      is_universal: true,
    },
  },
  'autumn_ticket': {
    'Осенний абонемент': {
      with_subscription: true,
      description: 'Следующее списание 2 970 ₽ через 3 месяца',
      offer_url: 'https://younesis.com/oferta_app',
      is_universal: true,
    },
    'Younesis Premium 12 месяцев': {
      with_subscription: true,
      description: 'Следующее списание 20 040 ₽ через 12 месяцев',
      offer_url: 'https://younesis.com/oferta_app',
      is_universal: true,
    },
  },
}

function buildPromoSubscriptionPriceList(subscriptionPriceList, slug) {
  const promoSubscriptions = slug
    ? (PROMO_SUBSCRIPTIONS_BY_SLUG[slug] ?? {})
    : PROMO_SUBSCRIPTIONS_DEFAULT

  return subscriptionPriceList
    .filter(item => promoSubscriptions[item.title])
    .map(item => {
      const promoConfig = promoSubscriptions[item.title]

      return {
        ...item,
        final_price: item.discounted_price ?? item.price,
        order: subscriptionFeatures[item.title]?.order,
        with_subscription: promoConfig.with_subscription,
        description: promoConfig.description,
        offer_url: promoConfig.offer_url,
        is_universal: Boolean(promoConfig.is_universal),
      }
    })
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
}

function buildPromoCampaignPriceList(campaigns) {
  return (campaigns ?? []).flatMap(campaign =>
    (campaign.prices ?? []).map(price => ({
      id: price.subscription_price_id,
      subscription_price_id: price.subscription_price_id,
      title: price.display_title ?? campaign.title,
      description: price.display_description,
      display_price: price.display_price,
      final_price: Number(price.price),
      currency: price.currency,
      campaign_id: campaign.id,
      campaign_slug: campaign.slug,
      offer_url: 'https://younesis.com/oferta_app',
    })),
  )
}


const axiosInstance = axios.create({
  baseURL: settings.env.VUE_APP_API_URL,
  headers: {
    'Accept': '*/*',
    'Content-Type': 'application/json',
  },
})

axiosInstance.interceptors.request.use(async (config) => {
  const value = store.state.user.token
  if (value) {
    config.headers['Authorization'] = `Token ${value}`
  }
  return config
})

axiosInstance.interceptors.response.use(undefined, async (error) => {

  if (!window.navigator.onLine && error.code === "ERR_NETWORK") {
    await store.dispatch('app/setOnlineState', false)
    return Promise.reject(error)
  }

  if (error?.response?.status === 404 && !settings.env.VUE_APP_IS_DEVELOPMENT) {
    router.push({name: '404'})
    return Promise.reject(error)
  }

  if (error?.response?.status === 401) {
    if (error.response?.data?.detail && error.response.data.detail === 'Invalid token.') {
      await store.dispatch('user/logout')
    }
    if (error.response?.data?.detail && error.response.data.detail === "User inactive or deleted.") {
      // todo: ?
      await store.dispatch('user/logout')
    }
    return Promise.reject(error)
  }

  return Promise.reject(error)
})

export const appAxios = {

  guest: {
    async fetchDirectories() {
      return await indexedStore.slices.base.indexingResponse('guest-fetchDirectories', axiosInstance({
        url: `/api/directories_unauth/`,
        method: 'GET',
      }))
    },

    getUserByPhone(phone) {
      return axiosInstance({
        url: `/api/user/get_user_by_phone/?phone=${phone}`,
        method: 'GET',
      })
    },

    getUser(id) {
      return axiosInstance({
        url: `/api/user/${id}/`,
        method: 'GET',
      })
    },

    getEvent(id) {
      return axiosInstance({
        url: `/api/event/${id}/`,
        method: 'GET',
      })
    },

    getCourse(id) {
      return axiosInstance({
        url: `/api/lms/courses/${id}/`,
        method: 'GET',
      })
    },

    getProduct(id) {
      return axiosInstance({
        url: `/api/product/${id}/`,
        method: 'GET',
      })
    },
  },

  cards: {
    delete(id) {
      return axiosInstance({
        url: `/api/cards/my/${id}/`,
        method: 'DELETE',
      })
    },
    setDefaultCard(id) {
      return axiosInstance({
        url: `/api/cards/my/${id}/set_default/`,
        method: 'POST',
        data: {}
      })
    },
  },

  subscription: {
    init(subscription_price_id, payment_type_id, payment_type_option_id, promo_code, card_id) {
      return axiosInstance({
        url: `/api/subscription/my/`,
        method: 'POST',
        data: {
          subscription_price_id,
          payment_type_id,
          payment_type_option_id,
          promo_code,
          card_id,
        }
      })
    },
    disable(id) {
      return axiosInstance({
        url: `/api/subscription/my/${id}/disable_auto_renewal/`,
        method: 'POST',
      })
    },
    fetch() {
      return axiosInstance({
        url: `/api/subscription/my/`,
        method: 'GET',
      })
    },
    userSubscriptions(id) {
      return axiosInstance({
        url: `/api/subscription/my/user_subscriptions/?user_id=${id}`,
        method: 'GET'
      })
    },
    initPayment(data, params) {
      const queryString = new URLSearchParams(params).toString()

      return axiosInstance({
        url: `/payment/pay/?${queryString}`,
        method: 'POST',
        data,
      })
    },
    // promoCodeCheck(data) {
    //   return axiosInstance({
    //     url: `/api/payment/promo-code/check/`,
    //     method: 'POST',
    //     data: {
    //       code: data.code,
    //       phone: data.phone,
    //     }
    //   })
    // },
    checkPromoCode(data) {
      return axiosInstance({
        url: `/api/check_promocode/`,
        method: 'POST',
        data: {
          code: data.code,
          phone: data.phone,
        }
      })
    },
    // Younesis: персональный список тарифов (сравниваем с directories.subscription_price_list)
    fetchPersonalPricesList(courseId) {
      const params = {}
      if (courseId != null && courseId !== '') {
        params.course_id = courseId
      }

      return axiosInstance({
        url: `/api/subscription/my/personal_prices_list/`,
        method: 'GET',
        params,
      })
    },
  },

  chat: {
    async chatLastMessage(date, lte = 'gte') {
      return await indexedStore.slices.base.indexingResponse2('chat-chatLastMessage', axiosInstance({
        url: `/api/chat/?updated_at__${lte}=${(new Date(date)).toISOString()}`,
        method: 'GET',
      }))
    },

    async fetchAll(params) {
      return await indexedStore.slices.base.indexingResponse2('chat-fetchAll', axiosInstance({
        url: `/api/chat/`,
        method: 'GET',
        params: params
      }))
    },

    async search(query) {
      return await axiosInstance({
        url: `/api/chat/search/`,
        method: 'POST',
        data: {
          search_query: query,
        }
      })
    },

    async start(user_to_id) {
      await indexedStore.slices.base.invalidate('chat-fetchAll')
      return await axiosInstance({
        url: `/api/chat/`,
        method: 'POST',
        data: {
          user_to_id
        }
      })
    },

    async fetchMessages(id, gteDate, lteDate) {
      const query = {
        chat_id: id,
      }
      if (gteDate) {
        query.created_at__gte = gteDate
      }
      if (lteDate) {
        query.created_at__lte = lteDate
      }
      const params = new URLSearchParams(query)
      // await indexedStore.slices.base.invalidate('chat-fetchAll')
      return await axiosInstance({
        url: `/api/chat-message/?${params.toString()}`,
        method: 'GET',
      })
    },

    async markChecked(chatId, messageIds) {
      await indexedStore.slices.base.invalidate('chat-fetchAll')
      return await axiosInstance({
        url: `/api/chat/${chatId}/mark_checked/`,
        method: 'POST',
        data: {
          message_ids: messageIds,
        }
      })
    }
  },

  events: {
    async fetchAll() {
      return indexedStore.slices.base.indexingResponse('events-fetchAll', axiosInstance({
        url: `/api/event/`,
        method: 'GET',
      }), 15 * TIME_UNITS.MINUTES)
    },
    fetchOne(id) {
      return axiosInstance({
        url: `/api/event/${id}/`,
        method: 'GET',
      })
    },
    async create(data) {
      await indexedStore.slices.base.invalidate('events-fetchAll')
      return axiosInstance({
        url: `/api/event/my/`,
        method: 'POST',
        data,
      })
    },
    fetchMy() {
      return axiosInstance({
        url: `/api/event/my/`,
        method: 'GET',
      })
    },
    fetchMyOne(id) {
      return axiosInstance({
        url: `/api/event/my/${id}/`,
        method: 'GET',
      })
    },
    fetchMyParticipants(id, limit = 25, offset = 0) {
      return axiosInstance({
        url: `/api/event/my/${id}/participants/?limit=${limit}&offset=${offset}`,
        method: 'GET',
      })
    },
    async update(id, data) {
      await indexedStore.slices.base.invalidate('events-fetchAll')
      return axiosInstance({
        url: `/api/event/my/${id}/`,
        method: 'PUT',
        data,
      })
    },
    async delete(id) {
      await indexedStore.slices.base.invalidate('events-fetchAll')
      return axiosInstance({
        url: `/api/event/my/${id}/`,
        method: 'DELETE',
      })
    },
    async join(id) {
      await indexedStore.slices.base.invalidate('events-fetchAll')
      return axiosInstance({
        url: `/api/event/${id}/join/`,
        method: 'POST',
      })
    },
    async leave(id) {
      await indexedStore.slices.base.invalidate('events-fetchAll')
      return axiosInstance({
        url: `/api/event/${id}/join/`,
        method: 'DELETE',
      })
    },
    /** Toggle избранного. Тело пустое; повторный POST снимает флаг. */
    async toggleFavorite(id) {
      await indexedStore.slices.base.invalidate('events-fetchAll')
      return axiosInstance({
        url: `/api/event/${id}/favorite/`,
        method: 'POST',
      })
    },
    fetchMyFavorites(limit = 50, offset = 0) {
      return axiosInstance({
        url: `/api/event/my/favorites/?limit=${limit}&offset=${offset}`,
        method: 'GET',
      })
    },
    /** .ics с Authorization (Chrome download по URL токен не шлёт). */
    fetchIcs(icsUrl) {
      return axiosInstance({
        url: icsUrl,
        method: 'GET',
        responseType: 'blob',
        headers: {
          Accept: 'text/calendar, text/plain, */*',
        },
      })
    },
  },

  auth: {
    sendAuthCode(payload) {
      return axiosInstance({
        url: `/api/auth/phone/send-code/`,
        method: 'POST',
        data: {
          ...payload
        }
      })
    },

    checkAuthCode(phone, code) {
      return axiosInstance({
        url: `/api/auth/phone/check-code/`,
        method: 'POST',
        data: {
          'phone': phone,
          'code': code
        }
      })
    },

    register(data) {
      return axiosInstance({
        url: `/api/register/`,
        method: 'POST',
        data: {
          'first_name': data.first_name,
          'last_name': data.last_name,
          'phone': data.phone
        }
      })
    },

    sendRegisterCode(phone) {
      return axiosInstance({
        url: `/api/register/phone/send-code/`,
        method: 'POST',
        data: {
          phone,
        }
      })
    },

    checkRegisterCode(phone, code) {
      return axiosInstance({
        url: `/api/register/phone/check-code/`,
        method: 'POST',
        data: {
          'phone': phone,
          'code': code
        }
      })
    },
  },

  specializations: {
    fetchAll() {
      return axiosInstance({
        url: `/api/specialization/my/`,
        method: 'GET',
      })
    },
    add({ title }) {
      return axiosInstance({
        url: `/api/specialization/my/`,
        method: 'POST',
        data: {
          title,
        },
      })
    },
    remove(id) {
      return axiosInstance({
        url: `/api/specialization/my/${id}/`,
        method: 'DELETE',
      })
    },
  },

  educations: {
    fetchAll() {
      return axiosInstance({
        url: `/api/education/my/`,
        method: 'GET',
      })
    },

    async create(values) {
      const formData = new FormData()
      const {images, ...data} = values

      if (images && Array.isArray(images)) {
        const toCreate = images.filter(i => i.changed && !i.isDeleted)
        toCreate.map(i => formData.append('images', i.file))
      }

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      return await axiosInstance({
        url: `/api/education/my/`,
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },

    async update(values) {
      const educationId = store.getters['user/education'].id
      const formData = new FormData()

      const {images, ...data} = values

      if (images && Array.isArray(images)) {
        const toRemove = images.filter(i => i.changed && i.isDeleted)
        const toCreate = images.filter(i => i.changed && !i.isDeleted)

        const deletePromises = toRemove.filter(i => i.id).map(i => {
          return appAxios.educations.deleteImage(data.education_type, i.id)
        })

        toCreate.map(i => formData.append('images', i.file))

        await Promise.all(deletePromises)
      }

      Object.keys(data).forEach(key => {
        if (data[key]) {
          formData.append(key, data[key])
        }
      })

      formData.append('instance_id', data.id)

      return await axiosInstance({
        url: `/api/education/my/${educationId}/`,
        method: 'PUT',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },

    async deleteImage(type, id) {
      return axiosInstance({
        url: `/api/${type}-image/${id}/`,
        method: 'DELETE',
      })
    },

    // router.register(r"api/community-image/", common.views.CommunityImageViewSet)
    // router.register(r"api/course-image/", common.views.CourseImageViewSet)
    // router.register(r"api/institution-image/", common.views.InstitutionImageViewSet)

    remove(education, education_type) {
      const educationId = store.getters['user/education'].id
      return axiosInstance({
        url: `/api/education/my/${educationId}/delete/`,
        method: 'POST',
        data: {
          instance_id: education.id,
          education_type: education_type,
        }
      })
    },
  },

  achievements: {
    fetchAll() {
      return axiosInstance({
        url: `/api/achievements/my/`,
        method: 'GET',
      })
    },

    create(text, order) {
      return axiosInstance({
        url: `/api/achievements/my/`,
        method: 'POST',
        data: {
          title: text,
          order: order
        },
      })
    },

    update(achievement) {
      return axiosInstance({
        url: `/api/achievements/my/${achievement.id}/`,
        method: 'PUT',
        data: {
          ...achievement,
        },
      })
    },

    remove(achievement) {
      return axiosInstance({
        url: `/api/achievements/my/${achievement.id}/`,
        method: 'DELETE',
      })
    },
  },

  niche: {
    fetchAll() {
      return axiosInstance({
        url: `/api/niche/my/`,
        method: 'GET',
      })
    },

    create(title) {
      return axiosInstance({
        url: `/api/niche/my/`,
        method: 'POST',
        data: {
          title,
        },
      })
    },

    remove(user_niche_id) {
      return axiosInstance({
        url: `/api/niche/my/${user_niche_id}/`,
        method: 'DELETE',
      })
    },
  },

  skills: {
    fetchAll() {
      return axiosInstance({
        url: `/api/skill/my/`,
        method: 'GET',
      })
    },

    create(title, order) {
      return axiosInstance({
        url: `/api/skill/my/`,
        method: 'POST',
        data: {
          title: title,
          order: order
        },
      })
    },

    remove(user_skill_id) {
      return axiosInstance({
        url: `/api/skill/my/${user_skill_id}/`,
        method: 'DELETE',
      })
    },

    update(user_skill_id, data) {
      return axiosInstance({
        url: `/api/skill/my/${user_skill_id}/`,
        method: 'PUT',
        data
      })
    },
  },

  profession: {
    fetchAll() {
      return axiosInstance({
        url: `/api/profession/my/`,
        method: 'GET',
      })
    },

    create(title) {
      return axiosInstance({
        url: `/api/profession/my/`,
        method: 'POST',
        data: {
          title,
        },
      })
    },

    remove(user_profession_id) {
      return axiosInstance({
        url: `/api/profession/my/${user_profession_id}/`,
        method: 'DELETE',
      })
    },
  },

  targets: {
    async update(data) {
      const profile = store.state.user.profile

      if (profile.targets.length) {
        profile.targets.forEach(target => {
          axiosInstance({
            url: `/api/target/my/${target.id}/`,
            method: 'DELETE',
            headers: {'Content-Type': 'multipart/form-data'},
          })
        })
      }

      const formData = new FormData()

      Object.keys(data).forEach((key, index) => {
        data.order = index + 1
        formData.append(key, data[key])
      })

      return axiosInstance({
        url: "/api/target/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
  },

  records: {
    async update(data) {
      const formData = new FormData()

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      return axiosInstance({
        url: "/api/personal-record/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
  },

  training_frequency: {
    async update(data) {
      const profile = store.state.user.profile

      if (profile.trainings_frequency.length) {
        profile.trainings_frequency.forEach(freq => {
          axiosInstance({
            url: `/api/training-frequency/my/${freq.id}/`,
            method: 'DELETE',
            headers: {'Content-Type': 'multipart/form-data'},
          })
        })
      }

      const formData = new FormData()

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      return axiosInstance({
        url: "/api/training-frequency/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
  },

  body_progress: {
    async update(data) {
      const formData = new FormData()

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      return axiosInstance({
        url: "/api/progress/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
  },

  ai_progress: {
    async create(data) {
      const formData = new FormData()
      formData.append('initial_img', data)

      return axiosInstance({
        url: "/api/generated-progress/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
    async update(payload) {
      const formData = new FormData()
      if (payload.file) {
        formData.append('initial_img', payload.file)
      }

      return axiosInstance({
        url: `/api/generated-progress/my/${payload.id}/`,
        method: 'PUT',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
    cancel(id) {
      return axiosInstance({
        url: `/api/generated-progress/my/${id}/cancel/`,
        method: 'POST',
      })
    },
    retry(id) {
      return axiosInstance({
        url: `/api/generated-progress/my/${id}/retry/`,
        method: 'POST',
      })
    },
  },

  health_service: {
    async update(data) {
      const profile = store.state.user.profile

      if (profile.health_services.length) {
        await axiosInstance({
          url: `/api/health-service/my/${profile.health_services[0].id}/`,
          method: 'DELETE',
          headers: {'Content-Type': 'multipart/form-data'},
        })
      }

      const formData = new FormData()

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      console.log('health_service after del')

      await axiosInstance({
        url: "/api/health-service/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
  },

  health_service_activity: {
    async update(data) {
      const profile = store.state.user.profile

      if (profile.activities.length) {
        await axiosInstance({
          url: `/api/activity/my/${profile.activities[0].id}/`,
          method: 'DELETE',
          headers: {'Content-Type': 'multipart/form-data'},
        })
      }

      const formData = new FormData()

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      await axiosInstance({
        url: "/api/activity/my/",
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },
  },

  user: {
    fetchAll() {
      return axiosInstance({
        url: "/api/user/",
        method: 'GET',
      })
    },

    search(data) {
      return axiosInstance({
        url: "api/user/search/",
        method: 'POST',
        data: {
          ...data,
        },
      })
    },

    searchPaginated(data, limit, offset) {
      return axiosInstance({
        url: `api/user/paginated_search/?limit=${limit}&offset=${offset}`,
        method: 'POST',
        data: {
          ...data,
          // "search_query": search_query ? search_query : undefined,
          // "professionIds": [],
          // "nicheIds": [],
          // "skillIds": [],
          // "ordering": "relative"
          //'rating', 'relative'
        }
      })
    },

    updateMyProfile(data) {
      const profile = store.state.user.profile
      const formData = new FormData()

      Object.keys(data).forEach(key => {
        formData.append(key, data[key])
      })

      return axiosInstance({
        url: `/api/user/my/${profile.id}/`,
        method: 'PUT',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },

    fetchMyProfile() {
      return axiosInstance({
        url: '/api/user/my/',
        method: 'GET',
      })
    },

    async fetchDirectories() {
      const request = axiosInstance({
        url: `/api/directories/`,
        method: 'GET',
      })
      // indexingResponse при cache-hit сразу отдаёт старый IndexedDB и пишет сеть в фоне.
      // Без event_cover_preset_list в кэше store остаётся без пресетов на всю сессию —
      // ждём свежий ответ, если в кэше списка ещё нет.
      const res = await indexedStore.slices.base.indexingResponse('user-fetchDirectories', request)
      if (!Array.isArray(res?.data?.event_cover_preset_list)) {
        return await request
      }

      return res
    },

    async fetchDirectoriesUnauth() {
      const res = await indexedStore.slices.base.indexingResponse2('user-fetchDirectories', axiosInstance({
        url: `/api/directories_unauth/`,
        method: 'GET',
      }))

      return res
    },

    async fetchSubscriptionUnauthPromo(slug) {
      const res = await indexedStore.slices.base.indexingResponse2('user-fetchSubscriptionUnauthPromo', axiosInstance({
        url: `/api/directories_unauth/`,
        method: 'GET',
      }))

      return buildPromoSubscriptionPriceList(res.response.data.subscription_price_list, slug)
    },

    // Пока не используем на /payment-form-promo — бэк не готов
    async fetchPromoCampaigns(slug) {
      const params = {}
      if (slug != null && slug !== '') {
        params.slug = slug
      }

      const res = await axiosInstance({
        url: '/api/promo-campaigns/',
        method: 'GET',
        params,
      })

      return buildPromoCampaignPriceList(res.data)
    },

    fetchProfile(userId) {
      return axiosInstance({
        url: `/api/user/${userId}/`,
        method: 'GET',
      })
    },
  },

  myModules: {
    get(moduleId) {
      return axiosInstance({
        url: `/api/lms/modules/my/${moduleId}/`,
        method: 'GET',
      })
    },

    start(ModuleId) {
      return axiosInstance({
        url: `/api/lms/modules/my/`,
        method: 'POST',
        data: {
          module_id: ModuleId,
        }
      })
    },

    finish(studentModuleId) {
      return axiosInstance({
        url: `/api/lms/modules/my/${studentModuleId}/finish/`,
        method: 'POST',
        data: {
          id: studentModuleId
        }
      })
    },
  },

  myLessons: {
    start(LessonId) {
      return axiosInstance({
        url: `/api/lms/lessons/my/`,
        method: 'POST',
        data: {
          lesson_id: LessonId,
        }
      })
    },

    finish(studentLessonId) {
      return axiosInstance({
        url: `/api/lms/lessons/my/${studentLessonId}/finish/`,
        method: 'POST',
        data: {
          id: studentLessonId
        }
      })
    }
  },

  myTest: {
    start(lesson_test_id) {
      return axiosInstance({
        url: `/api/lms/lesson/tests/my/`,
        method: 'POST',
        data: {
          lesson_test_id: lesson_test_id,
        }
      })
    },

    update(student_lesson_test_id, test_answers_ids) {
      return axiosInstance({
        url: `/api/lms/lesson/tests/my/${student_lesson_test_id}/`,
        method: 'PUT',
        data: {
          test_answers_ids: test_answers_ids,
        }
      })
    },

    finish(student_lesson_test_id) {
      return axiosInstance({
        url: `/api/lms/lesson/tests/my/${student_lesson_test_id}/finish/`,
        method: 'POST',
        data: {
          lesson_test_id: student_lesson_test_id,
        }
      })
    }
  },

  myCourses: {
    search(query) {
      return axiosInstance({
        url: `/api/lms/courses/my/search/`,
        method: 'POST',
        data: {
          search_query: query
        }
      })
    },

    finish(studentCourseId) {
      return axiosInstance({
        url: `/api/lms/courses/my/${studentCourseId}/finish/`,
        method: 'POST',
        data: {
          id: studentCourseId
        }
      })
    },

    async cacheOne(userCourseId) {
      return await indexedStore.slices.myCourses.indexingResponse(`myCourses-fetchOne-${userCourseId}`, axiosInstance, {
        url: `/api/lms/courses/my/${userCourseId}/`,
        method: 'GET',
      })
    },

    async fetchOne(userCourseId) {
      return axiosInstance({
        url: `/api/lms/courses/my/${userCourseId}/`,
        method: 'GET',
      })
      // return await indexedStore.slices.myCourses.indexingResponse(`myCourses-fetchOne-${userCourseId}`, axiosInstance({
      //   url: `/api/lms/courses/my/${userCourseId}/`,
      //   method: 'GET',
      // }))
    },

    async fetchAll(limit = 10, offset = 0) {
      return await axiosInstance({
        url: `/api/lms/courses/my/?limit=${limit}&offset=${offset}`,
        method: 'GET',
      })
    },

    async fetchFavorites(limit = 10, offset = 0, search = '') {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      })
      if (search) {
        params.set('search', search)
      }
      return await axiosInstance({
        url: `/api/lms/courses/my/favorites/?${params.toString()}`,
        method: 'GET',
      })
    },

    review(course_id, review_items) {
      return axiosInstance({
        url: `/api/lms/course-review/my/`,
        method: 'POST',
        data: {
          course_id,
          review_items,
        }
      })
    },
  },

  course: {
    search(query, type = 'course') {
      return axiosInstance({
        url: `/api/lms/courses/search/?type=${type}`,
        method: 'POST',
        data: {
          search_query: query
        }
      })
    },

    async fetchAll(limit = 10, offset = 0, type = 'course') {
      return await axiosInstance({
        url: `/api/lms/courses/?limit=${limit}&offset=${offset}&type=${type}`,
        method: 'GET',
      })
    },
    async cacheOne(courseId) {
      return await indexedStore.slices.myCourses.cacheResponse(`courses-fetchOne-${courseId}`, axiosInstance, {
        url: `/api/lms/courses/${courseId}/`,
        method: 'GET',
      })
    },
    fetchOne(courseId) {
      return axiosInstance({
        url: `/api/lms/courses/${courseId}/`,
        method: 'GET',
      })
    },
    async enroll(course_id) {
      await indexedStore.slices.base.invalidate('course-fetchAll')
      return axiosInstance({
        url: `/api/lms/courses/${course_id}/enroll/`,
        method: 'POST',
        data: {
          'email': 'mail@example.com',
        }
      })
    },
    async toggleFavorite(courseId) {
      await indexedStore.slices.myCourses.invalidate(`courses-fetchOne-${courseId}`)
      return axiosInstance({
        url: `/api/lms/courses/${courseId}/favorite/`,
        method: 'POST',
        data: {},
      })
    },
  },

  collections: {
    async fetchAll() {
      return await indexedStore.slices.base.indexingResponse('collections-fetchAll', axiosInstance({
        url: `/api/collections/`,
        method: 'GET',
      }))
    },
    async getById(id) {
      return await indexedStore.slices.base.indexingResponse2(`collections-getById-${id}`, axiosInstance({
        url: `/api/collections/${id}/`,
        method: 'GET',
      }))
    },
  },

  products: {
    fetchAll() {
      return axiosInstance({
        url: '/api/product/',
        method: 'GET',
      })
    },

    fetch(params) {
      return axiosInstance({
        url: '/api/product/',
        method: 'GET',
        params: {
          limit: params.limit,
          offset: params.offset,
          search: params.search
        }
      })
    },

    get(productId) {
      return axiosInstance({
        url: `/api/product/my/${productId}/`,
        method: 'GET',
      })
    },

    fetchMyAll() {
      return axiosInstance({
        url: `/api/product/my/`,
        method: 'GET',
      })
    },

    create(values) {
      const formData = new FormData()

      Object.keys(values).forEach(key => {
        formData.append(key, values[key])
      })

      return axiosInstance({
        url: `/api/product/`,
        method: 'POST',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },

    update(product) {
      const formData = new FormData()

      Object.keys(product).forEach(key => {
        formData.append(key, product[key])
      })

      return axiosInstance({
        url: `/api/product/my/${product.id}/`,
        method: 'PUT',
        data: formData,
        headers: {'Content-Type': 'multipart/form-data'},
      })
    },

    remove(product) {
      return axiosInstance({
        url: `/api/product/my/${product.id}/`,
        method: 'DELETE',
      })
    },
  },

  onboarding: {
    start(step) {
      return axiosInstance({
        url: '/api/onboarding/my/',
        method: 'POST',
        data: {
          step,
        }
      })
    },
    finish(id) {
      return axiosInstance({
        url: `/api/onboarding/my/${id}/`,
        method: 'PUT',
      })
    },
  },

  networking: {
    fetch(limit) {
      return axiosInstance({
        url: `/api/user/networking_profiles_limited/?limit=${limit}`,
        method: 'GET',
      })
    },

    async checkProfile(id) {
      await indexedStore.slices.base.invalidate('networking-fetchRecommendations')
      return axiosInstance({
        url: `/api/user-checked-profile/my/`,
        method: 'POST',
        data: {
          checked_profile_id: id,
        }
      })
    },

    async checkProfileReject(id) {
      await indexedStore.slices.base.invalidate('networking-fetchRecommendations')
      return axiosInstance({
        url: `/api/user-checked-profile/my/reject/`,
        method: 'POST',
        data: {
          checked_profile_id: id,
        }
      })
    },

    async fetchRecommendations() {
      return await indexedStore.slices.base.indexingResponse('networking-fetchRecommendations', axiosInstance({
        url: `/api/user/networking_profiles_limited/?limit=5`,
        method: 'GET',
      }))
    },
  },

  myContacts: {
    async fetchContact(id) {
      return axiosInstance({
        url: `/api/user-contact/my/?contact_user_id=${id}`,
        method: 'GET',
      })
    },

    async fetchContacts(limit = 10, offset = 0) {
      return await axiosInstance({
        url: `/api/user-contact/my/?limit=${limit}&offset=${offset}`,
        method: 'GET',
      })
    },

    async fetchInterests(limit = 10, offset = 0) {
      return await axiosInstance({
        url: `/api/user-contact/my/list_interest/?limit=${limit}&offset=${offset}`,
        method: 'GET',
      })
    },

    async fetchMatches(limit = 10, offset = 0) {
      return await axiosInstance({
        url: `/api/user-contact/my/list_match/?limit=${limit}&offset=${offset}`,
        method: 'GET',
      })
    },

    // async fetchAll(gteDate, limit, offset) {
    //   return await indexedStore.slices.base.indexingResponse('myContacts-fetchAll', axiosInstance({
    //     url: '/api/user-contact/my/',
    //     method: 'GET',
    //   }))
    // },

    searchPaginated(data, limit, offset) {
      return axiosInstance({
        url: `api/user-contact/my/paginated_search/?limit=${limit}&offset=${offset}`,
        method: 'POST',
        data: {
          ...data,
        }
      })
    },

    async addContact(userId, rating) {
      await indexedStore.slices.base.invalidate('myContacts-fetchContacts')
      await indexedStore.slices.base.invalidate('myContacts-fetchInterests')

      async function getCache(type) {
        const cache = await indexedStore.slices.base.get(`cardHolderCache-${type}`)
        if (!cache) {
          return null
        }
        if (cache.userId !== store.state.user.profile.id) {
          return null
        }
        return cache
      }

      async function setCache(type, data, skipCache) {
        const cache = await getCache(type)

        if (cache && !skipCache) {
          const usedIds = {}
          data.forEach(chat => usedIds[chat.id] = true)

          cache.data.forEach(chat => {
            if (!usedIds[chat.id]) {
              data.push(chat)
            }
          })
        }
        if (type !== 'contacts') {
          data = data
            .sort((a, b) => new Date(a.checked_at) - new Date(b.checked_at))
        }
        if (data.length) {
          const newCache = {
            userId: store.state.user.profile.id,
            lastUpdatedAtDate: data.at(0).updated_at,
            data,
          }
          await indexedStore.slices.base.set(`cardHolderCache-${type}`, newCache)

          return data
        } else {
          await indexedStore.slices.base.clear(`cardHolderCache-${type}`)
        }
        return []
      }

      const interestsCache = await getCache('interests')

      if (interestsCache) {
        const newC = await setCache('interests', interestsCache.data.filter(i => {
          return i.user.id !== userId
        }), true)
      }

      return await axiosInstance({
        url: '/api/user-contact/my/',
        method: 'POST',
        data: {
          'contact_user_id': userId,
          'rating': rating,
        }
      })
    },

    async updateContact(userContactId, rating) {
      await indexedStore.slices.base.invalidate('myContacts-fetchContacts')
      // await indexedStore.slices.base.invalidate('myContacts-fetchAll')
      return await axiosInstance({
        url: `/api/user-contact/my/${userContactId}/`,
        method: 'PUT',
        data: {
          'rating': rating,
        }
      })
    },

    async removeContact(userContactId, userID) {
      // console.log('userID', userID)
      await indexedStore.slices.base.invalidate('myContacts-fetchContacts')
      await indexedStore.slices.base.invalidate('myContacts-fetchMatchs')
      await indexedStore.slices.base.invalidate('myContacts-fetchInterests')

      async function getCache(type) {
        const cache = await indexedStore.slices.base.get(`cardHolderCache-${type}`)
        if (!cache) {
          return null
        }
        if (cache.userId !== store.state.user.profile.id) {
          return null
        }
        return cache
      }

      async function setCache(type, data, skipCache) {
        const cache = await getCache(type)

        if (cache && !skipCache) {
          const usedIds = {}
          data.forEach(chat => usedIds[chat.id] = true)

          cache.data.forEach(chat => {
            if (!usedIds[chat.id]) {
              data.push(chat)
            }
          })
        }
        if (type !== 'contacts') {
          data = data
            .sort((a, b) => new Date(a.checked_at) - new Date(b.checked_at))
        }
        if (data.length) {
          const newCache = {
            userId: store.state.user.profile.id,
            lastUpdatedAtDate: data.at(0).updated_at,
            data,
          }
          await indexedStore.slices.base.set(`cardHolderCache-${type}`, newCache)

          return data
        } else {
          await indexedStore.slices.base.clear(`cardHolderCache-${type}`)
        }
        return []
      }

      const contactsCache = await getCache('contacts')
      const matchCache = await getCache('match')

      // console.log('matchCache', matchCache)
      // console.log('userContactId', userContactId)
      // console.log('userID', userID)

      if (contactsCache) {
        await setCache('contacts', contactsCache.data.filter(i => {
          return i.id !== userContactId
        }), true)
      }
      if (matchCache) {
        await setCache('match', matchCache.data.filter(i => {
          return i.user.id !== userID
        }), true)
      }

      return await axiosInstance({
        url: `/api/user-contact/my/${userContactId}/`,
        method: 'DELETE',
      })
    },

    async markChecked(userContactId, type) {
      return await axiosInstance({
        url: `/api/user-contact/my/mark_checked/`,
        method: 'POST',
        data: {
          "user_contact_ids": [
            userContactId,
          ],
          "check_type": type,
          //match, interest
        }
      })
    },
  },

  roulette: {
    play() {
      return axiosInstance({
        url: `/api/lms/courses/my/play_roulette/`,
        method: 'POST',
      })
    },
    async check() {
      return await indexedStore.slices.base.indexingResponse('roulette-check', axiosInstance({
        url: `/api/lms/courses/my/check_roulette_play/`,
        method: 'GET',
      }))
    },
  },

  telegram: {
    send(initDataRaw, contact) {
      const init_data = (decodeURIComponent(initDataRaw)).replace(/"/g, '\"')
      const data = {init_data}

      if (contact) {
        data.contact = contact
      }

      return axiosInstance({
        url: `/api/auth/tg/`,
        method: 'POST',
        data,
      })
    },
  },

  premiumStatus: {
    getMyStatus() {
      return axiosInstance({
        url: '/api/premium-status/my/',
        method: 'GET',
      })
    },
    updateMyStatus(statusId) {
      return axiosInstance({
        url: '/api/premium-status/my/',
        method: 'POST',
        data: {
          premium_status_id: statusId
        },
      })
    },
  },

  my_training_programs: {
    create(data) {
      return axiosInstance({
        url: "/api/training-program/my/",
        method: 'POST',
        data
      })
    },
    remove(id) {
      return axiosInstance({
        url: `/api/training-program/my/${id}/`,
        method: 'DELETE'
      })
    },
    startTraining(id) {
      return axiosInstance({
        url: `/api/training/my/${id}/start/`,
        method: 'POST'
      })
    },
    restartTraining(id) {
      return axiosInstance({
        url: `/api/training/my/${id}/restart/`,
        method: 'POST'
      })
    },
    updateTraining(id, data) {
      return axiosInstance({
        url: `/api/training-exercise/my/${data.id}/`,
        method: 'PUT',
        data: {
          note: data.note
        }
      })
    },
    setExerciseSetFinish(id) {
      return axiosInstance({
        url: `/api/training-exercise/my/${id}/finish/`,
        method: 'POST'
      })
    },
    updateExercise({ id, payload }) {
      return axiosInstance({
        url: `/api/training-exercise/my/${id}/`,
        method: 'PUT',
        data: {
          ...payload
        }
      })
    }
  }
}
